import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Gift, ShoppingBag, UserPlus, Home, Phone, ChevronRight, Gamepad2, Crown, Wallet, Headphones } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import MiniRouletteLogo from './MiniRouletteLogo';

/**
 * Explore & Earn — hub page with tile grid linking to sub-pages.
 */
export default function ExploreEarnPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const phoneVerified = !!(user?.phone_verified || user?.phoneVerified || user?.phone_verified_at);

  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const basePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';

  const tiles = [
    {
      key: 'referral',
      label: 'Referral & Earn',
      sub: 'Invite friends — earn instantly',
      icon: UserPlus,
      gradient: 'linear-gradient(135deg, #063946 0%, #0A5B6A 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(224,244,247,0.24)',
      iconShadow: '0 7px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.18)'
    },
    {
      key: 'roulette',
      label: 'Roulette',
      sub: 'Spin to win every day',
      logo: 'roulette',
      gradient: 'linear-gradient(135deg, #063946 0%, #0A5B6A 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(224,244,247,0.24)',
      iconShadow: '0 7px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
    },
    {
      key: 'marketplace',
      label: 'Marketplace',
      sub: 'Spend coins on rewards',
      icon: ShoppingBag,
      gradient: 'linear-gradient(135deg, #063946 0%, #0A5B6A 100%)',
      iconColor: '#FFE66D',
      ring: 'rgba(224,244,247,0.24)',
      iconShadow: '0 7px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
    },
  ];

  return (
    <div
      className="player-app min-h-screen pb-32"
      style={{ color: '#FFFFFF' }}
      data-testid="explore-earn-page"
    >
      {/* Header — scrolls with content (matches home behavior) */}
      <div
        className="px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: '#000000',
        }}
      >
        <button
          onClick={() => navigate(homePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid="explore-earn-back"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1
            className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
            style={{ color: '#FFFFFF' }}
          >
            <Gift className="w-6 h-6" style={{ color: '#FFD93D' }} />
            Explore &amp; Earn
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Complete tasks. Unlock rewards.
          </p>
        </div>
      </div>

      {/* Tile grid */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {tiles.map((t) => {
            const Icon = t.icon;
            const isHot = false;
            return (
              <button
                key={t.key}
                onClick={() => navigate(`${basePath}/${t.key}`)}
                className={`text-left rounded-2xl p-5 sm:p-6 transition active:scale-[0.99] hover:brightness-110 flex items-center gap-4 relative overflow-hidden `}
                style={{
                  background: isHot
                    ? 'linear-gradient(135deg, rgba(255,107,0,0.18) 0%, rgba(255,26,26,0.10) 100%)'
                    : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${t.ring}`,
                  boxShadow: isHot
                    ? '0 0 0 1px rgba(255,107,0,0.45), 0 14px 36px rgba(255,107,0,0.28), inset 0 1px 0 rgba(255,255,255,0.10)'
                    : `0 8px 22px ${t.ring.replace('0.55', '0.18')}, inset 0 1px 0 rgba(255,255,255,0.05)`,
                }}
                data-testid={`explore-tile-${t.key}`}
              >
                {t.logo === 'roulette' ? (
                  <span
                    className="relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-[18px]"
                    style={{
                      background: t.gradient,
                      boxShadow: t.iconShadow || `0 8px 18px ${t.ring}, inset 0 1px 0 rgba(255,255,255,0.45)`,
                    }}
                    aria-hidden
                  >
                    <span className="absolute inset-[5px] rounded-[14px] bg-black/55 shadow-inner" />
                    <span className="absolute inset-0 rounded-[18px] ring-1 ring-white/30" />
                    <span className="relative grid h-11 w-11 place-items-center rounded-full bg-white/10">
                      <MiniRouletteLogo size={38} spin={false} />
                    </span>
                  </span>
                ) : (
                  <span
                    className="relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-[18px]"
                    style={{
                      background: t.gradient,
                      boxShadow: isHot
                        ? '0 12px 30px rgba(255,107,0,0.50), 0 0 28px rgba(255,217,61,0.34), inset 0 1px 0 rgba(255,255,255,0.55)'
                        : (t.iconShadow || `0 8px 18px ${t.ring}, inset 0 1px 0 rgba(255,255,255,0.45)`),
                    }}
                    aria-hidden
                  >
                    <span className="absolute inset-0 bg-[radial-gradient(circle_at_28%_18%,rgba(255,255,255,0.55),transparent_34%)]" />
                    <span className="absolute inset-[4px] rounded-[15px] border border-white/28" />
                    <Icon className="relative h-7 w-7 drop-shadow-[0_2px_8px_rgba(0,0,0,0.42)]" style={{ color: t.iconColor }} strokeWidth={2.65} />
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <p
                    className="display font-extrabold text-base sm:text-lg leading-tight"
                    style={{ color: '#FFFFFF' }}
                  >
                    {t.label}
                  </p>
                  <p
                    className="text-xs sm:text-sm leading-tight mt-0.5"
                    style={{ color: 'rgba(255,255,255,0.72)' }}
                  >
                    {t.sub}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Logged-in chip */}
        <div className="mt-8 flex justify-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px]"
            style={{
              background: 'rgba(0,77,97,0.10)',
              border: '1px solid rgba(0,77,97,0.30)',
              color: '#86EFAC',
            }}
          >
            Logged in as {user?.username || user?.email || 'player'}
          </span>
        </div>
      </div>

      <nav
        className="fixed bottom-0 left-0 right-0 z-[60] w-full px-0 pb-0"
        aria-label="Player quick navigation"
        data-testid="rewards-bottom-card-nav"
      >
        <div
          className="relative grid grid-cols-5 items-center rounded-t-[22px] rounded-b-none border px-2 py-2 shadow-2xl backdrop-blur-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(9,17,27,0.94), rgba(16,24,39,0.9))',
            border: '1.5px solid rgba(255,255,255,0.24)',
            boxShadow: '0 18px 42px rgba(0,0,0,0.56), 0 0 28px rgba(0,77,97,0.14), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-extrabold text-white transition active:scale-95"
            style={{ background: 'rgba(0,107,133,0.16)' }}
            data-testid="rewards-nav-rewards"
          >
            <Gift className="h-5 w-5" />
            <span>Rewards</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`${homePath}?open=games`)}
            className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-extrabold text-white/82 transition active:scale-95 hover:bg-white/8"
            data-testid="rewards-nav-games"
          >
            <Gamepad2 className="h-5 w-5" />
            <span>Games</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(homePath)}
            className="relative -mt-8 flex flex-col items-center justify-center gap-1"
            aria-label="Go home"
            data-testid="rewards-nav-logo"
          >
            <span
              className="grid h-[70px] w-[70px] place-items-center overflow-hidden rounded-full border bg-black/70 shadow-xl"
              style={{
                borderColor: 'rgba(255,255,255,0.78)',
                boxShadow: '0 0 0 5px rgba(0,107,133,0.28), 0 0 34px rgba(0,107,133,0.58), 0 0 28px rgba(0,77,97,0.2)',
              }}
            >
              <Crown className="h-8 w-8 text-white" />
            </span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`${homePath}?open=wallet`)}
            className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-extrabold text-white/82 transition active:scale-95 hover:bg-white/8"
            data-testid="rewards-nav-wallets"
          >
            <Wallet className="h-5 w-5" />
            <span>Wallets</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`${homePath}?open=support`)}
            className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-extrabold text-white/82 transition active:scale-95 hover:bg-white/8"
            data-testid="rewards-nav-support"
          >
            <Headphones className="h-5 w-5" />
            <span>Support</span>
          </button>
        </div>
      </nav>

    </div>
  );
}
